import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiShieldLine, RiSwapLine } from 'react-icons/ri';
import { Button } from '../../ComponnentsUI/common/Button';
import { Input } from '../../ComponnentsUI/common/Input';
import { FormField } from '../../ComponnentsUI/common/FormField';
import { Select } from '../../ComponnentsUI/common/Select';
import { useAdminUsers } from '../../../helpers/api/users/users';
import {
  useConfirmOwnershipTransfer,
  useInitiateOwnershipTransfer,
} from '../../../helpers/api/organization/organization';

const OwnershipTransferSettings = ({ currentUser }) => {
  const { t } = useTranslation('AccountPage');
  // Only the tenant owner sees the transfer UI; ownership is the is_tenant_owner flag.
  const isOwner = Boolean((currentUser as any)?.is_tenant_owner);
  const { data: adminUsers = [], isLoading: isAdminsLoading } = useAdminUsers({
    enabled: isOwner,
  });

  // Ownership can only go to an organization admin (the org_admin ACL role).
  const adminOptions = useMemo(() => {
    if (!Array.isArray(adminUsers)) return [];
    return adminUsers.filter(
      (user) =>
        user?.id !== currentUser?.id &&
        (user as any)?.is_tenant_owner !== true &&
        ((user as any)?.acl_roles ?? []).includes('Org Admin')
    );
  }, [adminUsers, currentUser?.id]);

  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [step, setStep] = useState<'idle' | 'verify'>('idle');
  const [verificationCode, setVerificationCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const initiateTransfer = useInitiateOwnershipTransfer(
    () => {
      setMessage(t('ownership.codeSent'));
      setError('');
      setStep('verify');
    },
    (err) => {
      setError(err?.message || t('ownership.initiateFailed'));
      setMessage('');
    },
    false
  );

  const confirmTransfer = useConfirmOwnershipTransfer(
    () => {
      setMessage(t('ownership.transferSuccess'));
      setError('');
      setStep('idle');
      setSelectedAdminId('');
      setVerificationCode('');
      setTotpCode('');
    },
    (err) => {
      setError(err?.message || t('ownership.confirmFailed'));
      setMessage('');
    },
    false
  );

  if (!isOwner) return null;

  const handleInitiate = (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!selectedAdminId) {
      setError(t('ownership.selectAdminError'));
      return;
    }
    initiateTransfer.mutate({ new_owner_user_id: selectedAdminId });
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!verificationCode) {
      setError(t('ownership.verificationRequired'));
      return;
    }
    if (currentUser?.is_2fa_enabled && !totpCode) {
      setError(t('ownership.twoFactorRequired'));
      return;
    }
    confirmTransfer.mutate({
      new_owner_user_id: selectedAdminId,
      verification_code: verificationCode,
      ...(currentUser?.is_2fa_enabled ? { totp_code: totpCode } : {}),
    });
  };

  return (
    <div className="bg-card-color-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 max-w-md">
      <div className="text-center mb-6">
        <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
          <RiShieldLine className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-2">
          {t('ownership.title')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('ownership.description')}
        </p>
      </div>

      {message && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-4">
          <p className="text-sm text-green-600 dark:text-green-400">{message}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {step === 'idle' ? (
        <form onSubmit={handleInitiate} className="space-y-4">
          <FormField label={t('ownership.newSuperAdmin')} htmlFor="new-owner">
            <Select
              id="new-owner"
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 p-2.5 bg-transparent dark:bg-gray-900 text-sm dark:text-white outline-none focus:ring-2 focus:ring-red-500"
              value={selectedAdminId}
              onChange={(e) => setSelectedAdminId(e.target.value)}
              disabled={isAdminsLoading}
              placeholder={isAdminsLoading ? t('ownership.loadingAdmins') : t('ownership.selectAdmin')}
              options={adminOptions.map((admin) => {
                const fullName = [admin.first_name, admin.last_name]
                  .filter(Boolean)
                  .join(' ')
                  .trim();
                return {
                  value: String(admin.id),
                  label: fullName ? `${fullName} (${admin.email})` : admin.email,
                };
              })}
            />
          </FormField>

          <Button
            type="submit"
            variant="destructive"
            size="md"
            className="w-full"
            disabled={!adminOptions.length || initiateTransfer.isPending}
          >
            <RiSwapLine className="w-4 h-4 mr-2" />
            {initiateTransfer.isPending ? t('ownership.sending') : t('ownership.sendVerificationCode')}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleConfirm} className="space-y-4">
          <FormField label={t('ownership.verificationCode')} htmlFor="ownership-code">
            <Input
              id="ownership-code"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              maxLength={6}
              minLength={6}
              placeholder="123456"
              className="text-center text-lg tracking-wider"
            />
          </FormField>

          {currentUser?.is_2fa_enabled && (
            <FormField label={t('ownership.twoFactorCode')} htmlFor="ownership-2fa-code">
              <Input
                id="ownership-2fa-code"
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                maxLength={6}
                minLength={6}
                placeholder="123456"
                className="text-center text-lg tracking-wider"
              />
            </FormField>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              variant="destructive"
              size="md"
              className="flex-1"
              disabled={confirmTransfer.isPending}
            >
              <RiSwapLine className="w-4 h-4 mr-2" />
              {confirmTransfer.isPending ? t('ownership.transferring') : t('ownership.confirmTransfer')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="md"
              className="flex-1"
              onClick={() => {
                setStep('idle');
                setVerificationCode('');
                setTotpCode('');
              }}
              disabled={confirmTransfer.isPending}
            >
              {t('ownership.cancel')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default OwnershipTransferSettings;
